'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';

// ============================================================
// Types
// ============================================================

interface ConciergeMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  isStreaming?: boolean;
}

interface AIConciergeProps {
  onClose?: () => void;
  embedded?: boolean;
}

// ============================================================
// Suggested Starter Prompts
// ============================================================

const STARTER_PROMPTS = [
  { icon: '✈️', label: 'Plan a luxury safari', prompt: 'Plan a 10-day luxury safari in East Africa for 2 people' },
  { icon: '🏝️', label: 'Maldives escape', prompt: 'Recommend the best overwater villas in the Maldives for a romantic getaway' },
  { icon: '🛩️', label: 'Private jet charter', prompt: 'What private jet options are available for a trip from London to Dubai?' },
  { icon: '💎', label: 'Membership benefits', prompt: 'What are the benefits of the Diamond membership tier?' },
];

// ============================================================
// Message Renderer
// ============================================================

function MessageBubble({ message }: { message: ConciergeMessage }) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      {/* Avatar */}
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
        isUser
          ? 'bg-primary/20 text-primary' :'bg-gradient-to-br from-amber-500/20 to-yellow-600/20 text-amber-400'
      }`}>
        {isUser ? <Icon name="UserCircleIcon" size={20} /> : '✦'}
      </div>

      {/* Bubble */}
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
          isUser
            ? 'bg-primary/15 text-foreground rounded-tr-sm'
            : 'border border-border/60 text-foreground rounded-tl-sm'
        }`} style={isUser ? {} : { background: 'var(--card)' }}>
          {message.isStreaming && !message.content ? (
            <div className="flex gap-1 items-center py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          ) : (
            <div className="whitespace-pre-wrap">{message.content}</div>
          )}
        </div>

        {/* Suggestions */}
        {message.suggestions && message.suggestions.length > 0 && !message.isStreaming && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {message.suggestions.map((s, i) => (
              <button
                key={`sug-${i}`}
                className="text-xs px-3 py-1.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                onClick={() => {
                  // Dispatch custom event to fill input
                  window.dispatchEvent(new CustomEvent('concierge-suggestion', { detail: s }));
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <span className="text-xs text-muted-foreground px-1">
          {message.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// Main Concierge Component
// ============================================================

export default function AIConcierge({ onClose, embedded = false }: AIConciergeProps) {
  const [messages, setMessages] = useState<ConciergeMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const [isAIConfigured, setIsAIConfigured] = useState<boolean | null>(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Check AI configuration on mount
  useEffect(() => {
    fetch('/api/ai/registry')
      .then((r) => r.json())
      .then((data) => {
        setIsAIConfigured(data?.data?.registry?.activeProviders > 0);
      })
      .catch(() => setIsAIConfigured(false));
  }, []);

  // Listen for suggestion clicks
  useEffect(() => {
    const handler = (e: Event) => {
      const suggestion = (e as CustomEvent).detail as string;
      setInput(suggestion);
      inputRef.current?.focus();
    };
    window.addEventListener('concierge-suggestion', handler);
    return () => window.removeEventListener('concierge-suggestion', handler);
  }, []);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Welcome message
  useEffect(() => {
    setMessages([{
      id: 'welcome',
      role: 'assistant',
      content: 'Welcome to WorldwayLuxe AI Concierge. I\'m here to help you plan extraordinary journeys, discover exclusive destinations, and navigate our luxury services. How may I assist you today?',
      timestamp: new Date(),
      suggestions: ['Plan a luxury trip', 'Explore destinations', 'View membership benefits'],
    }]);
  }, []);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || isStreaming) return;

    const userMsg: ConciergeMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date(),
    };

    const assistantMsgId = `assistant_${Date.now()}`;
    const assistantMsg: ConciergeMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsStreaming(true);
    setStreamingContent('');

    abortRef.current = new AbortController();

    try {
      const response = await fetch('/api/ai/concierge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText.trim(),
          session_id: sessionId,
          stream: true,
        }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.error === 'ai_not_configured') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsgId
                ? {
                    ...m,
                    content: errData.message || 'AI features are not yet configured. Please contact your administrator.',
                    isStreaming: false,
                    suggestions: errData.suggestions || [],
                  }
                : m
            )
          );
          setIsAIConfigured(false);
          return;
        }
        throw new Error(errData.message || `Request failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let accumulated = '';
      let finalSuggestions: string[] = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const parsed = JSON.parse(data);

            if (parsed.type === 'chunk' && parsed.content) {
              accumulated += parsed.content;
              setStreamingContent(accumulated);
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsgId ? { ...m, content: accumulated } : m
                )
              );
            }

            if (parsed.type === 'done') {
              finalSuggestions = parsed.suggestions || [];
            }

            if (parsed.type === 'error') {
              throw new Error(parsed.error || 'Stream error');
            }
          } catch (parseErr) {
            // Skip malformed SSE data
          }
        }
      }

      // Finalize message
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? { ...m, content: accumulated, isStreaming: false, suggestions: finalSuggestions }
            : m
        )
      );

    } catch (error) {
      if ((error as Error).name === 'AbortError') return;

      const errMsg = error instanceof Error ? error.message : 'Something went wrong';
      toast.error(errMsg);

      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMsgId
            ? {
                ...m,
                content: 'I apologise — I encountered an issue processing your request. Please try again or contact our concierge team directly.',
                isStreaming: false,
              }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
    }
  }, [sessionId, isStreaming]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    setIsStreaming(false);
  };

  const handleClear = async () => {
    await fetch(`/api/ai/concierge?session_id=${sessionId}`, { method: 'DELETE' });
    setMessages([{
      id: 'welcome-new',
      role: 'assistant',
      content: 'Conversation cleared. How may I assist you with your next luxury journey?',
      timestamp: new Date(),
    }]);
  };

  const containerClass = embedded
    ? 'flex flex-col h-full' :'flex flex-col h-full max-h-[85vh]';

  return (
    <div className={containerClass}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0"
        style={{ background: 'var(--card)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-yellow-600/20 flex items-center justify-center text-amber-400 text-lg">
            ✦
          </div>
          <div>
            <h2 className="font-serif text-base font-semibold text-foreground">AI Concierge</h2>
            <div className="flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${isAIConfigured === null ? 'bg-muted-foreground' : isAIConfigured ? 'bg-green-500' : 'bg-amber-500'}`} />
              <span className="text-xs text-muted-foreground">
                {isAIConfigured === null ? 'Connecting...' : isAIConfigured ? 'Gemini 2.5 Flash · Active' : 'Configuration required'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleClear}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-4 transition-colors"
            title="Clear conversation"
          >
            <Icon name="TrashIcon" size={16} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-4 transition-colors"
            >
              <Icon name="XMarkIcon" size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 min-h-0">
        {/* Starter prompts — show when only welcome message */}
        {messages.length <= 1 && (
          <div className="grid grid-cols-2 gap-2 mb-4">
            {STARTER_PROMPTS.map((sp, i) => (
              <button
                key={`sp-${i}`}
                onClick={() => sendMessage(sp.prompt)}
                className="flex items-start gap-2 p-3 rounded-xl border border-border/60 text-left hover:border-primary/40 hover:bg-primary/5 transition-all text-xs"
                style={{ background: 'var(--card)' }}
              >
                <span className="text-base flex-shrink-0">{sp.icon}</span>
                <span className="text-muted-foreground leading-snug">{sp.label}</span>
              </button>
            ))}
          </div>
        )}

        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-border px-4 py-3" style={{ background: 'var(--card)' }}>
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about destinations, itineraries, bookings..."
            disabled={isStreaming}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-border bg-surface-4 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 disabled:opacity-60 max-h-32 overflow-y-auto"
            style={{ minHeight: '42px' }}
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={handleStop}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-danger/20 text-danger-foreground hover:bg-danger/30 flex items-center justify-center transition-colors"
            >
              <Icon name="StopIcon" size={16} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="flex-shrink-0 w-10 h-10 rounded-xl btn-primary flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Icon name="PaperAirplaneIcon" size={16} />
            </button>
          )}
        </form>
        <p className="text-xs text-muted-foreground text-center mt-2">
          Press Enter to send · Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
